import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import NavLinks from '../NavLinks/NavLinks'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AddTagPopup from './AddTagPopup'
import Loader from '../Loader/Loader'
import Feedback from '../Feedback/Feedback'
import AuthenticatedRoutes from '../../Utils/AuthenticatedRoutes'
import { fetchTranscript, fetchEventTags } from '../../Api/transcriptAndSummary'
import { addPrompt, getVideoDownloadUrl } from '../../Api/meetings'
import './transcriptSummary.css'
import logAppEvent from '../../Api/appEventsLogging'
import { useSelector } from 'react-redux'
import { Platform } from '../../Context/PlatformContext'
import { ask } from '../../Api/search'
import Tag from './Tag'
import { Box, Flex, Input } from '@chakra-ui/react'
import { GetStartedContext } from '../../Context/GetStartedContext'

const monthsShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const Transcript = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(true)
  const [eventTags, setEventTags] = useState([])
  const [showAddTagPopup, setShowAddTagPopup] = useState(false)
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const user = useSelector((state) => state?.user?.data)
  const searchInputRef = useRef()

  const [search, setSearch] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [answer, setAnswer] = useState('')

  const [newPrompt, setNewPrompt] = useState('')
  const [newPromptLoading, setNewPromptLoading] = useState(false)

  const platform = useContext(Platform)
  const {currentStep} = useContext(GetStartedContext)

  const fetchTranscriptData = useCallback(async () => {
    const eventId = searchParams.get('eventId')
    let apiResponse = await fetchTranscript(eventId)
    if (apiResponse) {
      const transcripts = []
      apiResponse.transcripts?.map((transcript) => {
        let time =
          (Number(transcript.time) - Number(apiResponse?.event?.start_time)) /
          1000
        transcripts.push({
          ...transcript,
          time: `${
            parseInt(time / 60) < 10
              ? `0${parseInt(time / 60)}`
              : parseInt(time / 60)
          }:${
            parseInt(time % 60) < 10
              ? `0${parseInt(time % 60)}`
              : parseInt(time % 60)
          }`,
        })
      })
      const date = apiResponse.event?.start_time
        ? new Date(Number(apiResponse.event.start_time))
        : new Date()
      apiResponse.event.start = `${
        monthsShort[date.getMonth()]
      } ${date.getDate()}, ${date.getFullYear()}`
      setData({
        ...apiResponse,
        transcripts,
      })
    } else {
      setData(null)
    }
    if (isLoading) {
      setIsLoading(false)
    }
  }, [searchParams, setIsLoading, isLoading])

  const fetchEventTagsData = useCallback(async () => {
    if (searchParams.get('eventId')) {
      const apiResponse = await fetchEventTags(searchParams.get('eventId'))
      if (apiResponse?.length > 0) {
        setEventTags([...apiResponse])
      } else {
        setEventTags([])
      }
    }
  }, [searchParams, setEventTags])

  useEffect(() => {
    if (searchParams.get('eventId')) {
      fetchTranscriptData()
      fetchEventTagsData()
    } else {
      navigate(
        '/transcripts-summaries/transcript?eventId=latest-event&tab=HIGH'
      )
    }
  }, [
    searchParams.get('eventId'),
    fetchTranscriptData,
    fetchEventTagsData,
    navigate,
  ])

  const getVideoDownloadUrlHandler = useCallback(async () => {
    const apiResponse = await getVideoDownloadUrl(data.event.event_id)
    if (apiResponse) {
      const link = document.createElement('a')
      link.href = apiResponse
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [data])

  useEffect(() => {
    if (searchParams.get('transcriptId')) {
      const element = document.getElementById(
        `transcript-${searchParams.get('transcriptId')}`
      )
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [searchParams.get('transcriptId'), data])

  const handleToggle = (event) => {
    const eventId = searchParams.get('eventId')

    logAppEvent('transumm_summary_toggle_click', {
      user_id: user?.id,
      event_id: eventId,
    })
    navigate(
      `/transcripts-summaries/summary?eventId=${searchParams.get(
        'eventId'
      )}&tab=HIGH&eventName=${searchParams.get(
        'eventName'
      )}`
    )
  }

  const handleAddPrompt = async (prompt) => {
    setNewPromptLoading(true)
    const eventId = searchParams.get('eventId')
    await addPrompt(eventId, prompt, 'event')
    setNewPrompt('')
    setNewPromptLoading(false)
  }

  const submitHandler = useCallback(
    async (event) => {
      event.preventDefault()
      setIsSearchLoading(true)
      try {
        if (search.trim()) {
          const apiResponse = await ask(search.trim())
          setIsSearchLoading(false)
          if (apiResponse?.answer[0]?.answer) {
            setAnswer(apiResponse?.answer[0]?.answer)
          } else {
            setAnswer('')
          }
        }
      } catch (e) {
        // console.log(e)
        setIsSearchLoading(false)
      }
    },
    [search]
  )

  // useEffect(() => {
  //   document.addEventListener('keydown', (event) => {
  //     if (
  //       (platform === 'Mac' &&
  //         event.metaKey &&
  //         event.key.toUpperCase() === 'K') ||
  //       (platform === 'Windows' &&
  //         event.ctrlKey &&
  //         event.key.toUpperCase() === 'G')
  //     ) {
  //       event.preventDefault()
  //       setShowSearch(true)
  //       setSearch('')
  //       setAnswer('')
  //     }
  //     if (event.key.toUpperCase() === 'ESCAPE') {
  //       setShowSearch(false)
  //       setSearch('')
  //       setAnswer('')
  //     }
  //   })
  //   return () => document.removeEventListener('keydown', () => {})
  // }, [showSearch, setShowSearch])

  const handleShowSearchBar = () => {
    setShowSearch(true)
    searchInputRef.current.focus()
    setSearch('')
    setAnswer('')
  }

  return (
    <div className='container container_light ask transcript-summary'>
      {searchParams.get('transcriptId') && (
        <a
          id='transcript-scroll-tag'
          href={`#transcript-${searchParams.get('transcriptId')}`}
          style={{ display: 'none' }}
        ></a>
      )}
      <NavLinks />
      <AuthenticatedRoutes auth={true} />
      <div
        className={showSearch ? 'search-contanr active' : 'search-contanr'}
        style={{ marginLeft: '1rem' }}
      >
        <div
          onClick={() => {
            setShowSearch(false)
            setSearch('')
            setAnswer('')
          }}
          style={{
            height: '100%',
            width: '100%',
            position: 'absolute',
          }}
        />
        <div style={{ zIndex: 1 }} className='search-wrapper'>
          <div className='question-box'>
            <form onSubmit={submitHandler}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                ref={searchInputRef}
                placeholder='Enter question to Ask Arrowhead about meetings...'
              />
            </form>
            <div className='actions'>
              <h4>
                Searching within <span>{data?.event.title}</span>
              </h4>
              {/* <button>See previous questions</button> */}
            </div>
          </div>
          {isSearchLoading && <Loader />}
          {!isSearchLoading && answer && (
            <div className='answer-box'>
              <p>{answer}</p>
              <button>
                Ask another question{' '}
                {platform === 'Mac' ? (
                  <>
                    <span>⌘</span>
                    <span>K</span>
                  </>
                ) : (
                  <>
                    <span>Ctrl</span>
                    <span>G</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      {isLoading ? (
        <Loader />
      ) : (
        <div className='transript-summary-container'>
          {data?.event && (
            <>
              <h1>{data?.event?.title}</h1>
              <div className='tags-section'>
                <div className='download-section'>
                  <h3>{data?.event?.start && data?.event?.start}</h3>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* <Tag
                      eventId={data?.event?.event_id}
                      tags={eventTags}
                      fetch={fetchEventTagsData}
                    /> */}
                    {data.event.video_url && (
                      <button onClick={getVideoDownloadUrlHandler}>
                        <img src='/assets/icons/download.svg' alt='' />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className='switch-box'>
                  <h3>Summary</h3>
                  <label className='switch'>
                    <input checked type='checkbox' onChange={handleToggle} />
                    <span className='slider round'></span>
                  </label>
                  <h3 className='strong'>Transcript</h3>
                </div>
                <div
                  className='asktsbar'
                  style={{ marginLeft: '1rem', cursor: 'pointer' }}
                  onClick={handleShowSearchBar}
                >
                  <div className='tag'>Ask Arrowhead</div>
                  {platform === 'Mac' ? (
                    <>
                      <div className='key'>⌘</div>
                      <div className='key'>K</div>
                    </>
                  ) : (
                    <>
                      <div className='key'>Ctrl</div>
                      <div className='key'>G</div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          {data?.transcripts?.length > 0 ? (
            <div className='content summary'>
              {data?.transcripts?.map((transcript, index) => (
                <p
                  id={`transcript-${transcript.id}`}
                  key={transcript.id}
                  className={
                    searchParams.get('transcriptId') == transcript.id
                      ? 'active'
                      : ''
                  }
                >
                  <strong>{`${transcript.user} (${transcript.time})`}: </strong>
                  {transcript.transcript}
                </p>
              ))}
            </div>
          ) : (
            <div className='data-notfound'>
              <strong>
                Looks like our Arrowhead Notetaker didn’t enter this meeting!
              </strong>
              <p>
                Allow ‘Arrowhead Notetaker’ into every meeting so we can
                auto-build your knowledge repository for you!
              </p>
              <p>(We promise not to interrupt your conversation 😉)</p>
            </div>
          )}
        </div>
      )}
      <Feedback reference_id={searchParams.get('eventId')} feedback_type={3} />

      {/* {currentStep === -1 && (
        <>
          <Box mb={12} />
          <Flex position='fixed' zIndex={20} left={0} bottom={4} w='full' justifyContent='center'>
            <Input
              bg='white'
              border='0.5px solid rgba(28, 28, 28, 0.1)'
              boxShadow='0px 16px 10px rgba(2, 13, 49, 0.02), 0px 7px 7px rgba(2, 13, 49, 0.03), 0px 2px 4px rgba(2, 13, 49, 0.03), 0px 0px 0px rgba(2, 13, 49, 0.03)'
              _focusVisible={{
                border: '0.5px solid rgba(28, 28, 28, 0.1)',
                boxShadow: '0px 16px 10px rgba(2, 13, 49, 0.02), 0px 7px 7px rgba(2, 13, 49, 0.03), 0px 2px 4px rgba(2, 13, 49, 0.03), 0px 0px 0px rgba(2, 13, 49, 0.03)'
              }}
              flex={1}
              maxW='707px'
              color='black'
              transition='none'
              value={newPrompt} 
              placeholder='Try asking “what are the blockers”'
              onChange={e => !setNewPromptLoading || setNewPrompt(e.target.value)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddPrompt(newPrompt)
                }
              }}
            />
          </Flex>
        </>
      )} */}
    </div>
  )
}

export default Transcript
